<script setup lang="ts">
import { onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";

import type { CompanyProfile } from "@/entities/company";
import { useSessionStore } from "@/entities/session";
import { SalonProfileForm } from "@/features/salon-profile";
import { SalonGalleryPanel } from "@/features/salon-gallery";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  FormAlert,
  Skeleton,
} from "@/shared/ui";

import { useSalonProfile } from "../model/useSalonProfile";

/**
 * Host-компонент `/salon-profile` (spec §5, §2.4). Single-scroll, без табів.
 * Паралельно завантажує company + photos; гейтить 2 незалежні Card-и.
 * Header-sync (D10): після save → `session.fetchCompany()` (помилка — тихо).
 */

const { t } = useI18n();
const session = useSessionStore();

const {
  profileStatus,
  galleryStatus,
  company,
  photos,
  load,
  reloadGallery,
  setCompany,
  setPhotos,
} = useSalonProfile();

const announceMsg = ref("");

function announce(msg: string): void {
  announceMsg.value = msg;
}

async function onProfileSaved(updated: CompanyProfile): Promise<void> {
  setCompany(updated);
  try {
    await session.fetchCompany();
  } catch {
    // save was successful; header syncs on next bootstrap (D10)
  }
}

onMounted(() => {
  void load();
});
</script>

<template>
  <div class="mx-auto max-w-content flex flex-col gap-6">
    <!-- a11y polite region — reorder/announce (spec §9) -->
    <p
      role="status"
      aria-live="polite"
      class="sr-only"
    >
      {{ announceMsg }}
    </p>

    <!-- loading: skeleton h1 row + 2 card placeholders -->
    <div
      v-if="profileStatus === 'loading'"
      aria-busy="true"
      class="flex flex-col gap-6"
      data-testid="salon-host-loading"
    >
      <span class="sr-only">{{ t("salon.loading") }}</span>
      <Skeleton class="h-7 w-48" />
      <Skeleton class="h-40 w-full rounded-lg" />
      <Skeleton class="h-40 w-full rounded-lg" />
    </div>

    <template v-else>
      <h1 class="text-xl font-semibold">
        {{ t("salon.title") }}
      </h1>

      <!-- error: profile load failed + retry -->
      <template v-if="profileStatus === 'error'">
        <FormAlert
          variant="destructive"
          :message="t('salon.loadError')"
          data-testid="salon-host-error"
        />
        <div>
          <Button
            variant="outline"
            size="sm"
            @click="load()"
          >
            {{ t("salon.retry") }}
          </Button>
        </div>
      </template>

      <!-- ready: 2 cards (profile form + gallery) -->
      <template v-else>
        <Card data-testid="section-profile">
          <CardHeader>
            <CardTitle>{{ t("salon.profile.title") }}</CardTitle>
          </CardHeader>
          <CardContent>
            <SalonProfileForm
              :company="company!"
              @saved="onProfileSaved"
            />
          </CardContent>
        </Card>

        <Card data-testid="section-gallery">
          <CardHeader>
            <CardTitle>{{ t("salon.gallery.title") }}</CardTitle>
          </CardHeader>
          <CardContent>
            <SalonGalleryPanel
              :photos="photos"
              :gallery-status="galleryStatus"
              @retry="reloadGallery"
              @changed="setPhotos"
              @announce="announce"
            />
          </CardContent>
        </Card>
      </template>
    </template>
  </div>
</template>
